import { css } from "@linaria/core";
import React, { useMemo } from "react";
import { useProject } from "../../../../api/useProjects";
import { useQuantities, type Quantity } from "../../../../api/useQuantities";
import { type ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import { FaultEventLoading } from "../../Events/components/FaultEventLoading";
import useAnalysesPeriod from "../../Events/hooks/useAnalysesPeriod";
import useSeries, {
  getColorLossesRepartition,
  getPodiumSystems,
  getOtherSystems,
} from "../../Events/hooks/charts/lossesRepartition/useSeries";
import { useIndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import useOptions from "../../Events/hooks/charts/lossesRepartition/useOptions";
import { colors, spacings } from "@ui/Theme";
import HighchartsComponent from "@ui/Highcharts";
import { useSetups } from "../../../../api/useSetups";
import { useMetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import useLossesBySystem from "../../Events/hooks/useLossesBySystem";
import { computeInternalGapsOverPeriodActiveAnalyses } from "../../../../domain/aggregation/internalGapOverPeriod";
import { SquareSymbol } from "@ui/Highcharts/ui/Legend";
import Text from "@ui/Text";
import t from "../../../../i18n";
import NoDataPeriodPlaceholderWidget from "../NoDataPeriodPlaceholderWidget";
import formatNumber from "@ui/i18n/formatNumber";
import { useTranslations } from "../../../../api/useTranslations";
import type { Event } from "../../../../api/useEvents";

const isAroundZero = (mean: number) => {
  const absoluteValue = Math.abs(mean);
  return absoluteValue > 0 && absoluteValue < 0.1;
};

const ChartLossesRepartition = ({
  aggregationInterval,
  projectId,
  performanceIndicator,
  activeAnalyses,
  events,
  loading,
}: {
  aggregationInterval: { from: Date; to: Date };
  projectId: string;
  performanceIndicator: Quantity;
  activeAnalyses: ActiveAnalysis[];
  events: Event[];
  loading?: boolean;
}) => {
  const project = useProject(projectId);
  if (!project) {
    throw new Error("invalid project");
  }
  const translations = useTranslations();
  const quantities = useQuantities(projectId);
  const setups = useSetups(projectId);
  const metrologyOverrides = useMetrologyOverrides(projectId);
  const indexedFailureValues = useIndexedFailureValues(projectId);
  const chartRef = React.useRef<Highcharts.Chart>(null);

  const { activeAnalysesPeriod } = useAnalysesPeriod(aggregationInterval, activeAnalyses);
  const activeAnalyseDatesPeriod = useMemo(
    () => activeAnalysesPeriod.map(a => a.date),
    [activeAnalysesPeriod],
  );
  const meanLossesBySystem = useLossesBySystem({
    quantities,
    activeAnalyseDatesPeriod,
    aggregationInterval,
    indexedFailureValues,
    events,
  });

  const meanInternalLosses = useMemo(
    () =>
      computeInternalGapsOverPeriodActiveAnalyses(
        performanceIndicator,
        setups,
        metrologyOverrides,
        activeAnalysesPeriod,
      ),
    [performanceIndicator, setups, metrologyOverrides, activeAnalysesPeriod],
  );

  const meanExplicatedLosses = useMemo(
    () => meanLossesBySystem.reduce((acc, value) => acc + value.averageLosses, 0),
    [meanLossesBySystem],
  );

  const series = useSeries({
    meanLossesBySystem,
    meanExplicatedLosses,
    meanInternalLosses,
  });

  const legends = useMemo(() => {
    const unit = performanceIndicator.unit;

    const unExplicatedLosses = meanInternalLosses
      ? meanInternalLosses.meanLosses - meanExplicatedLosses
      : null;
    if (meanInternalLosses === null || unExplicatedLosses === null) return null;

    const podiums = getPodiumSystems(meanLossesBySystem);
    const systemPodiums = podiums
      .sort((a, b) => a.averageLosses - b.averageLosses)
      .map((s, index) => ({
        label: s.system,
        value: s.averageLosses,
        color: getColorLossesRepartition(index),
      }));
    const others = getOtherSystems(meanLossesBySystem);
    const otherSystems = [];
    if (others.length > 0) {
      const value = others.reduce((acc, value) => acc + value.averageLosses, 0);
      otherSystems.push({
        label: t("project.fault_events.charts.losses_repartition.label.other_systems"),
        value,
        color: getColorLossesRepartition(3),
      });
    }

    const losses = [
      ...systemPodiums,
      ...otherSystems,
      {
        label: t(
          "project.fault_events.charts.losses_repartition.label.unexplained_losses",
          translations,
        ),
        value: unExplicatedLosses,
        color: getColorLossesRepartition(4),
      },
    ].filter(f => f.value !== 0);
    if (losses.length === 0) return null;

    const legends = [
      ...losses,
      {
        label: t("project.fault_events.charts.losses_repartition.label.internal_losses"),
        value: meanInternalLosses.meanLosses,
        color: getColorLossesRepartition(5),
      },
    ];

    return legends.map(m => {
      const around = isAroundZero(m.value);
      return {
        ...m,
        label: m.label,
        valueNumber: m.value,
        value: `${around ? "≈ " : ""}${formatNumber(m.value, { precision: around ? 0 : m.value === 0 ? 0 : 1, unit })}`,
      };
    });
  }, [
    performanceIndicator,
    meanInternalLosses,
    meanLossesBySystem,
    meanExplicatedLosses,
    translations,
  ]);

  const isAllAroundZero = useMemo(
    () => legends !== null && legends.length > 0 && legends.every(l => isAroundZero(l.valueNumber)),
    [legends],
  );

  const options = useOptions({ series, performanceIndicator });

  if (loading) return <FaultEventLoading className={extension} />;
  if (legends === null || legends.length === 0)
    return <NoDataPeriodPlaceholderWidget className={extension} />;

  return (
    <div className={barChart}>
      {isAllAroundZero ? (
        <Text className={allAroundZero} type="body2">
          {t("project.fault_events.charts.losses_repartition.all_around_zero")}
        </Text>
      ) : (
        <HighchartsComponent
          className={featureHighcharts}
          name="losses-repartition"
          ref={chartRef}
          options={options}
          selectablePoints={false}
        />
      )}
      <div className={featureLegends}>
        {legends.map(legend => (
          <div key={legend.label} className={featureLegend}>
            <SquareSymbol className={curveSquareSymbol} color={legend.color} />
            <Text type="body2" title={legend.label}>
              {legend.label}
            </Text>
            <Text type="body2SemiBold">({legend.value})</Text>
          </div>
        ))}
      </div>
    </div>
  );
};
export default ChartLossesRepartition;

//#region CSS
const extension = css`
  flex: 1;
`;
const barChart = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  flex: 1;
  height: 100%;
`;
const featureHighcharts = css`
  min-height: 116px;
  min-width: 200px;
`;
const allAroundZero = css`
  height: 84px;
  display: grid;
  place-items: center;
  color: ${colors.carbon500};
`;
const featureLegends = css`
  height: fit-content;
  margin-top: ${spacings.xs};
  display: flex;
  flex-wrap: wrap;
  gap: ${spacings.s};
`;
const featureLegend = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
  color: ${colors.carbon800};
  margin-left: 8px;

  & > :nth-child(1) {
    flex-shrink: 0;
  }
  & > :nth-child(2) {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 100px;
  }
  & > :nth-child(3) {
    white-space: nowrap;
  }
`;
const curveSquareSymbol = css`
  border-radius: 2px;
`;
//#endregion CSS
